import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Sheet } from '../../components/Sheet';
import { Button } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { useT } from '../../i18n';

const input = 'h-12 w-full rounded-xl border border-ink-700 bg-ink-900 px-4 text-center font-bold uppercase tracking-widest outline-none focus:border-enemy';
const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').trim().toUpperCase();

/** The runner deletes their own account: they type the confirmation word first. */
export function DeleteAccountSheet({ open, onClose, onDeleted }: { open: boolean; onClose: () => void; onDeleted: () => void }) {
  const t = useT();
  const [typed, setTyped] = useState('');
  const word = t('DELETE');
  const del = useMutation({
    mutationFn: () => api('/me', { method: 'DELETE', body: { confirm: 'DELETE' } }),
    onSuccess: onDeleted,
  });
  const close = () => (setTyped(''), del.reset(), onClose());

  return (
    <Sheet open={open} onClose={close} label={t('Delete account')}>
      <div className="space-y-4" data-testid="delete-account-sheet">
        <h2 className="font-display text-2xl font-bold">{t('Delete your account?')}</h2>
        <p className="text-sm text-ink-300">{t('This permanently deletes your profile, runs, XP, awards and connections. Your sectors go back to neutral. This cannot be undone.')}</p>
        <p className="text-sm text-ink-300">{t('Clubs and events you own are handed over to another member or organiser so the others can keep playing.')}</p>
        <label className="block text-sm text-ink-300">
          {t('Type {word} to confirm', { word })}
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            className={`mt-2 ${input}`}
            data-testid="delete-account-confirm"
          />
        </label>
        {del.error ? <p className="text-sm text-enemy">{del.error instanceof ApiError ? del.error.message : t('Something went wrong')}</p> : null}
        <Button variant="danger" className="w-full" disabled={fold(typed) !== fold(word)} loading={del.isPending} onClick={() => del.mutate()} data-testid="delete-account-submit">
          {t('Delete my account')}
        </Button>
        <Button variant="ghost" className="w-full" onClick={close}>
          {t('Cancel')}
        </Button>
      </div>
    </Sheet>
  );
}
